import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import UsageService, { UsageData } from "../../services/Usage";
import { useUserSession } from "../UserSessionContext";
import { logAndFormatFormError } from "../../utils/Errors";

interface UsageContextType {
  usageRecords: UsageData[];
  loading: boolean;
  error: string | null;
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
  filters: {
    platformId?: string;
    startDate?: string;
    endDate?: string;
    sortBy: string;
    sortOrder: "asc" | "desc";
  };
  setFilters: (filters: Partial<UsageContextType["filters"]>) => void;
  setPage: (page: number) => void;
  setLimit: (limit: number) => void;
  refreshUsageData: () => Promise<void>;
  createUsageRecord: (usageData: UsageData) => Promise<any>;
  updateUsageRecord: (
    id: string,
    usageData: Partial<UsageData>
  ) => Promise<any>;
  deleteUsageRecord: (id: string) => Promise<any>;
}

const defaultPagination = {
  total: 0,
  page: 1,
  limit: 10,
  pages: 0,
};

const defaultFilters: UsageContextType["filters"] = {
  sortBy: "date",
  sortOrder: "desc" as "asc" | "desc",
};

const UsageContext = createContext<UsageContextType>({
  usageRecords: [],
  loading: false,
  error: null,
  pagination: defaultPagination,
  filters: defaultFilters,
  setFilters: () => {},
  setPage: () => {},
  setLimit: () => {},
  refreshUsageData: async () => {},
  createUsageRecord: async () => ({}),
  updateUsageRecord: async () => ({}),
  deleteUsageRecord: async () => ({}),
});

export const UsageProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [usageRecords, setUsageRecords] = useState<UsageData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState(defaultPagination);
  const [filters, setFiltersState] = useState(defaultFilters);

  const { userSession } = useUserSession();

  const setFilters = useCallback((newFilters: Partial<typeof filters>) => {
    setFiltersState((prevFilters) => ({
      ...prevFilters,
      ...newFilters,
    }));
    // Reset to page 1 when filters change
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  const setPage = useCallback((page: number) => {
    setPagination((prev) => ({ ...prev, page }));
  }, []);

  const setLimit = useCallback((limit: number) => {
    setPagination((prev) => ({ ...prev, limit, page: 1 }));
  }, []);

  const refreshUsageData = useCallback(async () => {
    if (!userSession?.id) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const usageService = new UsageService();
      const response = await usageService.getAllUsage(
        {
          page: pagination.page,
          limit: pagination.limit,
          sortBy: filters.sortBy,
          sortOrder: filters.sortOrder,
          platformId: filters.platformId,
          startDate: filters.startDate,
          endDate: filters.endDate,
        },
        userSession.id
      );

      if (response.success) {
        setUsageRecords(response.data || []);
        setPagination({
          total: response.pagination.total,
          page: response.pagination.page,
          limit: response.pagination.limit,
          pages: response.pagination.pages,
        });
      } else {
        setError(response.error || "Failed to fetch usage data");
      }
    } catch (error) {
      const errorMessage = logAndFormatFormError(
        "UsageContext.refreshUsageData",
        error,
        "Fetching usage data"
      );
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [userSession, pagination.page, pagination.limit, filters]);

  const createUsageRecord = useCallback(
    async (usageData: UsageData) => {
      if (!userSession?.id) {
        throw new Error("User session is not available");
      }

      try {
        const usageService = new UsageService();
        const response = await usageService.createUsage(
          usageData,
          userSession.id
        );

        if (response.success) {
          // Refresh data after successful creation
          await refreshUsageData();
        }

        return response;
      } catch (error) {
        const errorMessage = logAndFormatFormError(
          "UsageContext.createUsageRecord",
          error,
          "Creating usage record"
        );
        throw new Error(errorMessage);
      }
    },
    [userSession, refreshUsageData]
  );

  const updateUsageRecord = useCallback(
    async (id: string, usageData: Partial<UsageData>) => {
      if (!userSession?.id) {
        throw new Error("User session is not available");
      }

      try {
        const usageService = new UsageService();
        const response = await usageService.updateUsage(
          id,
          usageData,
          userSession.id
        );

        if (response.success) {
          // Refresh data after successful update
          await refreshUsageData();
        }

        return response;
      } catch (error) {
        const errorMessage = logAndFormatFormError(
          "UsageContext.updateUsageRecord",
          error,
          "Updating usage record"
        );
        throw new Error(errorMessage);
      }
    },
    [userSession, refreshUsageData]
  );

  const deleteUsageRecord = useCallback(
    async (id: string) => {
      if (!userSession?.id) {
        throw new Error("User session is not available");
      }

      try {
        const usageService = new UsageService();
        const response = await usageService.deleteUsage(id, userSession.id);

        if (response.success) {
          // Refresh data after successful deletion
          await refreshUsageData();
        }

        return response;
      } catch (error) {
        const errorMessage = logAndFormatFormError(
          "UsageContext.deleteUsageRecord",
          error,
          "Deleting usage record"
        );
        throw new Error(errorMessage);
      }
    },
    [userSession, refreshUsageData]
  );

  // Effect to fetch data when dependencies change
  useEffect(() => {
    if (userSession?.id) {
      refreshUsageData();
    }
  }, [
    userSession,
    pagination.page,
    pagination.limit,
    filters,
    refreshUsageData,
  ]);

  const value = {
    usageRecords,
    loading,
    error,
    pagination,
    filters,
    setFilters,
    setPage,
    setLimit,
    refreshUsageData,
    createUsageRecord,
    updateUsageRecord,
    deleteUsageRecord,
  };

  return (
    <UsageContext.Provider value={value}>{children}</UsageContext.Provider>
  );
};

export const useUsage = () => useContext(UsageContext);

export default UsageContext;
